import { Injectable } from '@angular/core';
import { sortBy } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { AccountIalEnum } from '../models/account.model';
import { DictionaryCategory, DictionaryModel, SimpleDictionaryModel } from '../models/dictionary.model';
import { SharedApiService } from './interfaces/shared-api.service';

/**
 * Used in app.module.ts to load dictionary when app is initialized
 *
 * @param provider Dictionary service
 */
export function dictionaryProviderFactory(provider: DictionaryService): () => Promise<DictionaryModel[]> {
  return () => provider.load();
}

@Injectable({
  providedIn: 'root'
})
export class DictionaryService {

  private dictionary = new BehaviorSubject<DictionaryModel[]>([]);

  constructor(
    private api: SharedApiService
  ) { }

  /** Used by factory to get dictionary when app is initialized */
  load(): Promise<DictionaryModel[]> {

    return new Promise((resolve) => {
      this.api.getPublic<DictionaryModel[]>('/api/v1/dictionary')
        .subscribe(res => {
          this.dictionary.next(res);
          resolve(res);
        });
    })
  }

  getSecurityLevels(): DictionaryModel[] {
    return this.getCollectionFromDictionary(DictionaryCategory.SECURITY_LEVEL.toString())
      .sort((a, b) =>
        a.id === AccountIalEnum.HIGH || b.id === AccountIalEnum.LOW ? 1
          : a.id === AccountIalEnum.LOW || b.id === AccountIalEnum.HIGH ? -1 : 0);
  }

  getNameFromDictionary(name: string, dictionary: DictionaryModel[]): string {
    const singleRow = dictionary.find(d => d.id === name);
    return singleRow ? singleRow.name : name;
  }

  getNameFromExistedStream(name: string): string {
    return this.getNameFromDictionary(name, this.dictionary.getValue());
  }

  getApplicationNameFromDictionary(applicationName: string, dictionary: DictionaryModel[]): string {
    const singleRow = dictionary.find(d => d.id === applicationName);
    return singleRow ? singleRow.applicationName : applicationName;
  }
  
  getApplicationNameFromExistedStream(applicationName: string): string {
    return this.getApplicationNameFromDictionary(applicationName, this.dictionary.getValue());
  }

  getCollectionFromDictionarySortedByName(name: string): DictionaryModel[] {
    return this.dictionary.getValue().filter(r => r.type === name).sort((x,y) => {
      return x.name.localeCompare(y.name)
    })
  }

  getSortedByName(category: DictionaryCategory): DictionaryModel[] {
    return this.getCollectionFromDictionarySortedByName(category.toString());
  }

  getApplications(): Observable<SimpleDictionaryModel[]> {
    return this.api.get<SimpleDictionaryModel[]>('/api/v1/dictionary/applications');
  }

  getAbpbRoles(): Observable<SimpleDictionaryModel[]> {
    return this.api.get<SimpleDictionaryModel[]>('/api/v1/dictionary/roles?type=ABPB');
  }

  private getCollectionFromDictionary(name: string): DictionaryModel[] {
    return this.dictionary.getValue().filter(r => r.type === name);
  }
}
